import {useEffect, useRef, useState} from "react";
import {Button, Drawer, HStack, Icon, Input, Portal, Spinner, Table, Text, VStack} from "@chakra-ui/react";
import {IoCloseSharp} from "react-icons/io5";
import ScenarioService from "@/services/agency/ScenarioService";
import {showToast} from "@/components/ui/toaster";

const PAGE_SIZE = 10;

const AssignScenarioToClientModal = ({isOpen, onClose, clientId, onAssigned}) => {
    const [scenarios, setScenarios] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [assigning, setAssigning] = useState(null);
    const debounceRef = useRef(null);

    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    useEffect(() => {
        if (!isOpen) return;
        setScenarios([]);
        setPage(1);
        setSearch('');
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        setIsLoading(true);
        ScenarioService.getWithoutOwner(page, PAGE_SIZE, search)
            .then(res => {
                setScenarios(res.items ?? []);
                setTotalCount(res.totalCount ?? 0);
            })
            .catch(() => showToast("Błąd", "Nie udało się pobrać scenariuszy", "error"))
            .finally(() => setIsLoading(false));
    }, [isOpen, page, search]);

    const handleSearchChange = (e) => {
        const val = e.target.value;
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setPage(1);
            setSearch(val);
        }, 350);
    };

    const handleAssign = async (scenarioId) => {
        setAssigning(scenarioId);
        try {
            await ScenarioService.assignToOwner(scenarioId, clientId);
            showToast("Scenariusz przypisany", null, "success");
            onAssigned();
            onClose();
        } catch {
            showToast("Błąd", "Nie udało się przypisać scenariusza", "error");
        } finally {
            setAssigning(null);
        }
    };

    return (
        <Drawer.Root open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="xl" placement="end">
            <Portal>
                <Drawer.Backdrop/>
                <Drawer.Positioner padding="4">
                    <Drawer.Content minW="100%" bg="bg.drawer" borderRadius={16}>
                        <Drawer.Header>
                            <HStack justifyContent="space-between" w="full">
                                <Text color="white" fontWeight={700} fontSize="lg">Przypisz scenariusz</Text>
                                <Icon fontSize={32} color="text.grey" onClick={onClose} cursor="pointer">
                                    <IoCloseSharp/>
                                </Icon>
                            </HStack>
                        </Drawer.Header>
                        <Drawer.Body>
                            <VStack w={{sm: 'full', xl: '70%'}} mx="auto" align="flex-start" gap={4}>
                                <Input
                                    variant="primary"
                                    placeholder="Szukaj scenariusza..."
                                    onChange={handleSearchChange}
                                />
                                <Text color="text.grey" fontSize="sm">{totalCount} scenariuszy</Text>
                                {isLoading ? (
                                    <HStack w="full" justify="center" py={8}>
                                        <Spinner color="white" size="lg"/>
                                    </HStack>
                                ) : scenarios.length === 0 ? (
                                    <Text color="text.grey" py={8} w="full" textAlign="center">
                                        Brak dostępnych scenariuszy
                                    </Text>
                                ) : (
                                    <VStack w="full" align="flex-start" gap={0}>
                                        <Table.Root w="full">
                                            <Table.Header>
                                                <Table.Row bg="bg.card" borderBottom="1px solid" borderColor="border.primary">
                                                    {['Nazwa', 'Agent', 'Utworzono', ''].map((col, i) => (
                                                        <Table.ColumnHeader
                                                            key={i}
                                                            color="text.grey"
                                                            fontWeight={600}
                                                            fontSize="xs"
                                                            textTransform="uppercase"
                                                            letterSpacing="wider"
                                                            py={3}
                                                            px={5}
                                                            border="none"
                                                            textAlign={i === 3 ? 'right' : 'left'}
                                                        >
                                                            {col}
                                                        </Table.ColumnHeader>
                                                    ))}
                                                </Table.Row>
                                            </Table.Header>
                                            <Table.Body>
                                                {scenarios.map((s) => (
                                                    <Table.Row
                                                        key={s.id}
                                                        bg="bg.secondary"
                                                        borderBottom="1px solid"
                                                        borderColor="border.primary"
                                                        _hover={{bg: "bg.highlight"}}
                                                        transition="background 0.15s"
                                                    >
                                                        <Table.Cell py={4} px={5} border="none">
                                                            <Text color="white" fontSize="sm" fontWeight={500}>{s.name}</Text>
                                                        </Table.Cell>
                                                        <Table.Cell py={4} px={5} border="none">
                                                            <Text color={s.agent ? "white" : "text.grey"} fontSize="sm">
                                                                {s.agent?.name ?? "—"}
                                                            </Text>
                                                        </Table.Cell>
                                                        <Table.Cell py={4} px={5} border="none">
                                                            <Text color="text.grey" fontSize="sm">
                                                                {new Date(s.createdAtUtc).toLocaleDateString('pl-PL')}
                                                            </Text>
                                                        </Table.Cell>
                                                        <Table.Cell py={4} px={5} border="none" textAlign="right">
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                onClick={() => handleAssign(s.id)}
                                                                disabled={assigning !== null}
                                                                loading={assigning === s.id}
                                                            >
                                                                Przypisz
                                                            </Button>
                                                        </Table.Cell>
                                                    </Table.Row>
                                                ))}
                                            </Table.Body>
                                        </Table.Root>
                                        <HStack justify="space-between" w="full" pt={4}>
                                            <Button
                                                variant="outlined"
                                                size="sm"
                                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                                disabled={page === 1}
                                            >
                                                Poprzednia
                                            </Button>
                                            <Text color="text.grey" fontSize="sm">
                                                Strona {page} / {totalPages}
                                            </Text>
                                            <Button
                                                variant="outlined"
                                                size="sm"
                                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                                disabled={page >= totalPages}
                                            >
                                                Następna
                                            </Button>
                                        </HStack>
                                    </VStack>
                                )}
                            </VStack>
                        </Drawer.Body>
                        <Drawer.Footer borderRadius={16}>
                            <HStack justifyContent="flex-end" w="full">
                                <Button variant="outlined" textDecoration="unset" onClick={onClose}>Anuluj</Button>
                            </HStack>
                        </Drawer.Footer>
                    </Drawer.Content>
                </Drawer.Positioner>
            </Portal>
        </Drawer.Root>
    );
};

export default AssignScenarioToClientModal;
